import React, { useEffect, useState } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Plus, Trash2, Eye, EyeOff, Upload } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdvertisementManagement = () => {
  const { getAuthHeaders } = useAdminAuth();
  const { toast } = useToast();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    link_url: '',
    image_url: '',
    position: 'sidebar',
    active: true,
  });

  useEffect(() => {
    fetchAds();
  }, []);

  const fetchAds = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/advertisements`, getAuthHeaders());
      setAds(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch advertisements', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    uploadFormData.append('category', 'ads');

    try {
      const response = await axios.post(
        `${API_URL}/api/admin/upload/image`,
        uploadFormData,
        {
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      setFormData({ ...formData, image_url: response.data.url });
      toast({ title: 'Success', description: 'Advertisement image uploaded' });
    } catch (error) {
      toast({ title: 'Error', description: 'Image upload failed', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/admin/advertisements`, formData, getAuthHeaders());
      toast({ title: 'Success', description: 'Advertisement created successfully' });
      resetForm();
      fetchAds();
      setShowModal(false);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save advertisement', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this advertisement?')) return;

    try {
      await axios.delete(`${API_URL}/api/admin/advertisements/${id}`, getAuthHeaders());
      toast({ title: 'Success', description: 'Advertisement deleted' });
      fetchAds();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete advertisement', variant: 'destructive' });
    }
  };

  const toggleActive = async (id, currentStatus) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/advertisements/${id}`,
        { active: !currentStatus },
        getAuthHeaders()
      );
      toast({ title: 'Success', description: `Advertisement ${!currentStatus ? 'activated' : 'deactivated'}` });
      fetchAds();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      link_url: '',
      image_url: '',
      position: 'sidebar',
      active: true,
    });
  };

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api/uploads') || url.startsWith('/uploads')) return `${API_URL}${url}`;
    return url;
  };

  return (
    <div className="p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Advertisement Management</CardTitle>
            <CardDescription>Upload and manage advertisement posters</CardDescription>
          </div>
          <Button onClick={() => setShowModal(true)} className="bg-red-600 hover:bg-red-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Advertisement
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p>Loading...</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {ads.map((ad) => (
                <Card key={ad.id} className="overflow-hidden">
                  <div className="relative">
                    <img
                      src={getImageUrl(ad.image_url)}
                      alt={ad.title}
                      className="w-full h-48 object-cover"
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/400x300?text=Ad+Image'; }}
                    />
                    <div className="absolute top-2 right-2">
                      <Badge className={ad.active ? 'bg-green-600' : 'bg-gray-600'}>
                        {ad.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-bold mb-1">{ad.title}</h3>
                    <p className="text-xs text-gray-500 mb-2">{ad.position}</p>
                    {ad.link_url && (
                      <a
                        href={ad.link_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline block mb-3 truncate"
                      >
                        {ad.link_url}
                      </a>
                    )}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleActive(ad.id, ad.active)}
                        className="flex-1"
                      >
                        {ad.active ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                        {ad.active ? 'Hide' : 'Show'}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(ad.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Advertisement</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Advertisement Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Tripura Tourism Ad"
                required
              />
            </div>

            <div>
              <Label>Advertisement Image *</Label>
              <Input type="file" accept="image/*" onChange={handleImageUpload} required={!formData.image_url} />
              {uploading && <p className="text-sm text-gray-500 mt-1">Uploading...</p>}
              {formData.image_url && (
                <img
                  src={getImageUrl(formData.image_url)}
                  alt="Preview"
                  className="mt-2 max-h-48 rounded border"
                />
              )}
            </div>

            <div>
              <Label>Link URL (Optional)</Label>
              <Input
                value={formData.link_url}
                onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                placeholder="https://example.com"
              />
              <p className="text-xs text-gray-500 mt-1">Users will be redirected here when clicking the ad</p>
            </div>

            <div>
              <Label>Position *</Label>
              <select
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              >
                <option value="sidebar">Sidebar</option>
                <option value="header">Header Banner</option>
                <option value="footer">Footer</option>
                <option value="between-articles">Between Articles</option>
              </select>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading || uploading}>
                {loading ? 'Creating...' : 'Create Advertisement'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdvertisementManagement;
